import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Calendar } from 'lucide-react';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string | null;
  content: string;
  image_url: string | null;
  created_at: string;
}

const News = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      const { data } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false })
        .limit(3);
      
      setPosts(data || []);
      setLoading(false);
    };

    fetchPosts();
  }, []);

  if (loading) {
    return (
      <section id="aktuelt" aria-labelledby="news-heading" className="section-padding bg-cream">
        <div className="max-w-7xl mx-auto">
          <h2 id="news-heading" className="text-3xl md:text-4xl font-serif text-foreground text-center mb-12">
            Aktuelt
          </h2>
          <div className="flex justify-center">
            <p className="text-muted-foreground" role="status">Laster...</p>
          </div>
        </div>
      </section>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <section id="aktuelt" aria-labelledby="news-heading" className="section-padding bg-cream">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-primary font-sans text-xs tracking-[0.3em] uppercase mb-4">
            Siste nytt
          </p>
          <h2 id="news-heading" className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
            Aktuelt
          </h2>
          <p className="text-muted-foreground font-sans">
            Siste nytt fra Bunadstuggu
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post) => (
            <Link
              key={post.id}
              to={`/blogg/${post.id}`}
              className="group block bg-card rounded-lg shadow-soft overflow-hidden hover:shadow-medium transition-shadow duration-300"
            >
              {post.image_url && (
                <div className="aspect-video overflow-hidden">
                  <img
                    src={post.image_url}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                  <Calendar className="w-4 h-4" aria-hidden="true" />
                  <time dateTime={post.created_at}>
                    {new Date(post.created_at).toLocaleDateString('nb-NO', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </time>
                </div>
                <h3 className="text-xl font-serif text-foreground mb-3 group-hover:text-primary transition-colors">
                  {post.title}
                </h3>
                <p className="text-muted-foreground line-clamp-3 font-sans text-sm">
                  {post.excerpt || post.content.substring(0, 150)}...
                </p>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/aktuelt"
            className="inline-block border border-foreground/30 text-foreground px-8 py-3 text-sm tracking-widest uppercase hover:bg-foreground hover:text-background transition-colors duration-300 font-sans"
          >
            Se alle innlegg
          </Link>
        </div>
      </div>
    </section>
  );
};

export default News;
